// src/components/common/Button.js

import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';

// Styles de base pour les boutons et liens
const baseButtonStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 30px; /* Bordures arrondies comme demandé */
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .icon {
    margin-left: 8px;
  }
`;

// Composant bouton
const StyledButton = styled(motion.button)`
  ${baseButtonStyles}
  padding: ${props => props.size === 'large' ? '1rem 2.5rem' : props.size === 'small' ? '0.5rem 1rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.size === 'large' ? '1.1rem' : props.size === 'small' ? '0.8rem' : '1rem'};
  border: 2px solid transparent;
  
  background-color: ${props => {
    if (props.variant === 'outline') return 'transparent';
    if (props.variant === 'secondary') return props.theme.accent;
    if (props.variant === 'success') return props.theme.success;
    return props.theme.primary;
  }};
  
  color: ${props => props.variant === 'outline' ? props.theme.primary : '#ffffff'};
  
  border-color: ${props => {
    if (props.variant === 'outline') return props.theme.primary;
    if (props.variant === 'secondary') return props.theme.accent;
    if (props.variant === 'success') return props.theme.success;
    return props.theme.primary;
  }};
`;

// Composant lien (pour React Router)
const StyledLink = styled(motion(Link))`
  ${baseButtonStyles}
  padding: ${props => props.size === 'large' ? '1rem 2.5rem' : props.size === 'small' ? '0.5rem 1rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.size === 'large' ? '1.1rem' : props.size === 'small' ? '0.8rem' : '1rem'};
  border: 2px solid transparent;
  
  background-color: ${props => {
    if (props.variant === 'outline') return 'transparent';
    if (props.variant === 'secondary') return props.theme.accent;
    if (props.variant === 'success') return props.theme.success;
    return props.theme.primary;
  }};
  
  color: ${props => props.variant === 'outline' ? props.theme.primary : '#ffffff'};
  
  border-color: ${props => {
    if (props.variant === 'outline') return props.theme.primary;
    if (props.variant === 'secondary') return props.theme.accent;
    if (props.variant === 'success') return props.theme.success;
    return props.theme.primary;
  }};
`;

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  to = null,
  onClick,
  icon,
  ...props
}) => {
  const { theme } = useContext(ThemeContext);
  
  // Animation props
  const motionProps = {
    whileHover: { scale: disabled ? 1 : 1.03 },
    whileTap: { scale: disabled ? 1 : 0.98 }
  };

  // Si c'est un lien
  if (to) {
    return (
      <StyledLink
        to={to}
        variant={variant}
        size={size}
        theme={theme}
        {...motionProps}
        {...props}
      >
        {children}
        {icon && <span className="icon">{icon}</span>}
      </StyledLink>
    );
  }
  
  // Sinon c'est un bouton
  return (
    <StyledButton
      disabled={disabled}
      variant={variant}
      size={size}
      onClick={onClick}
      theme={theme}
      {...motionProps}
      {...props}
    >
      {children}
      {icon && <span className="icon">{icon}</span>}
    </StyledButton>
  );
};

export default Button;